import { Box, DialogContent, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
} from "react";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
} from "../../../../../models/user/DynamicForm/DynamicForm";
import {
  GetFODwellingDetailsFormJson,
  UpdateFODwellingDetails,
} from "../../../../../store/user/policyChange/FOPolicyChangeActions";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import { GetPolicyRisks, GetPolicyDetailsHeader } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { IFODwellingDetailsUpdateRequestParams } from "../../../../../models/user/PolicyChange/FOPolicyChange";
import { setFODwellingDetailsFormFieldsJson } from "../../../../../store/user/policyChange/FOPolicyChangeSlice";
import { getCookie, formatDateToYYYYMMDD } from "../../../../../utils/Utility";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
  onClose?: () => void; // Add this prop to close the main drawer
}

const UpdateDwellingDetails: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  let { policyId, lobCode } = useParams();

  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);

  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
      dispatch(GetPolicyDetailsHeader({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const { t } = useTranslation();

  const { foDwellingDetailsFormFieldsJson } = useAppSelector(
    (state: RootState) => state.foPolicyChange
  );

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );
  
  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      // Add any async functions if needed for FO dwelling details
    }),
    []
  );

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
    if (onClose) {
      onClose();
    }
  }, [dispatch, onClose]);

  const handleUpdateDwellingDetailsFormOnSubmit = (formValues: {
    [key: string]: any;
  }) => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader?.carrierPolicyNumber &&
      userDetails &&
      userDetails.customerId &&
      policyId &&
      system &&
      carrier &&
      lobCode
    ) {
      const changeDate = formValues.effectiveDate
        ? formatDateToYYYYMMDD(new Date(formValues.effectiveDate))
        : formatDateToYYYYMMDD(new Date());
      
      const requestParams: IFODwellingDetailsUpdateRequestParams = {
        carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
        changeDt: changeDate,
        customerId: userDetails?.customerId,
        policyRef: policyId,
        carrier: carrier,
        system: system,
        lobCode: lobCode,
        detailDescription: "Updating FO dwelling details from UI",
        dwellingDetails: {
          constructionType: formValues.constructionType,
          roofType: formValues.roofType,
          squareFootage: Number(formValues.squareFootage),
          dwellingType: formValues.dwellingType,
        },
      };

      dispatch(UpdateFODwellingDetails(requestParams));
    }
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "foPolicyChange/UpdateFODwellingDetails"
    ) {
      setMessageDialogOpen(true);
      dispatch(setCalledReducerType(null));
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(GetFODwellingDetailsFormJson());
      dispatch(setCalledReducerType(null));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (foDwellingDetailsFormFieldsJson) {
      setFormFieldsJson(foDwellingDetailsFormFieldsJson);
    }
  }, [foDwellingDetailsFormFieldsJson]);

  // Cleanup effect to reset form fields when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setFODwellingDetailsFormFieldsJson(null));
    };
  }, [dispatch]);

  return (
    <>
      <MessageDialog
        handleClose={handleMessageDialogClose}
        open={messageDialogOpen}
      />
      <DialogContent
        id="update_dwelling_details_form_content"
        data-test-id="update_dwelling_details_form_content"
        aria-label="Update dwelling details form content"
      >
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleUpdateDwellingDetailsFormOnSubmit}
            asyncOptionsFunctions={asyncFunctions}
          />
        ) : (
          <Box sx={{ padding: "2rem", textAlign: "center" }}>
            <Typography>Loading form...</Typography>
          </Box>
        )}
      </DialogContent>
    </>
  );
};

export default UpdateDwellingDetails; 